import { Add, KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { Box, Select, Stack, Typography, Option, IconButton } from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import Color from "color";
import { add, format, parse, sub } from "date-fns";
import { ApiTaskSet } from "griptonite-api-client/types/tasks";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useMeProfileQuery } from "@/hooks/profile";
import { useCalendarSearchParams } from "@/routing/gym.$venueUuid/schedule/calendar";

import { useVenueTaskSets } from "../../hooks/tasks";
import { useCurrentVenueUuid, useVenueStaffQuery } from "../../hooks/venues";
import { StaffItem } from "../forms/StaffSelect";
import { ButtonLink } from "../Links";
import TaskSetDetailSidebar from "../TaskSetDetailSidebar/TaskSetDetailSidebar";
import { CalendarContextMenu } from "./CaledarContextMenu";
import Calendar from "./Calendar";
import { CalendarContextProvider, useCalendar } from "./CalendarContext";
import EventsSidebar from "./EventsSidebar";
import { TasksetEventCard, taskSetColorMap } from "./TaskSetEventCard";

type SidebarProps = {
  date?: string;
  events?: ApiTaskSet[];
  onCloseEvents: () => void;
};

const Sidebar = ({ date, events, onCloseEvents }: SidebarProps) => {
  const { editingSetUuid, newSetValues, setNewSetValues, setEditingSetUuid } = useCalendar();
  if (newSetValues) {
    return (
      <TaskSetDetailSidebar
        createNew
        newSetValues={newSetValues}
        onClose={() => setNewSetValues(null)}
      />
    );
  } else if (editingSetUuid) {
    return <TaskSetDetailSidebar uuid={editingSetUuid} onClose={() => setEditingSetUuid(null)} />;
  } else if (events && events.length && date) {
    return <EventsSidebar date={date} onClose={onCloseEvents} events={events} />;
  } else {
    return null;
  }
};

const MonthSwitcher = () => {
  const { month } = useCalendarSearchParams();
  const startOfMonth = parse(month, "yyyy-MM", new Date());
  const nextMonth = format(add(startOfMonth, { months: 1 }), "yyyy-MM");
  const prevMonth = format(sub(startOfMonth, { months: 1 }), "yyyy-MM");
  const formattedMonth = format(startOfMonth, "MMM yy");

  // use datefns to get the beginning of the prev and next months

  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <ButtonLink
        size="md"
        sx={{
          borderRadius: "100%",
          padding: 0.25,
          minHeight: "24px",
          minWidth: "24px",
        }}
        variant="soft"
        color="neutral"
        from="/gym/$venueUuid/schedule/calendar"
        to="."
        search={(prev) => ({ ...prev, month: prevMonth })}
        replace
      >
        <KeyboardArrowLeft />
      </ButtonLink>
      <Typography level="body-sm" fontWeight="bold" sx={{ minWidth: "60px", textAlign: "center" }}>
        {formattedMonth}
      </Typography>
      <ButtonLink
        size="md"
        sx={{
          borderRadius: "100%",
          padding: 0.25,
          minHeight: "24px",
          minWidth: "24px",
        }}
        variant="soft"
        color="neutral"
        from="/gym/$venueUuid/schedule/calendar"
        to="."
        search={(prev) => ({ ...prev, month: nextMonth })}
        replace
      >
        <KeyboardArrowRight />
      </ButtonLink>
    </Stack>
  );
};

const AssigneeSelector = () => {
  const currentVenueUuid = useCurrentVenueUuid();
  const { assignee: selectedAssignee } = useCalendarSearchParams();
  const { data: staff } = useVenueStaffQuery(currentVenueUuid);
  const { data: meProfile } = useMeProfileQuery();
  const navigate = useNavigate({ from: "/gym/$venueUuid/schedule/calendar" });

  const assigneeChoices = useMemo(() => {
    const assignees = new Map();
    if (staff?.find((staffInfo) => staffInfo.user.uuid === meProfile?.uuid)) {
      assignees.set(meProfile?.uuid, meProfile);
    }
    // TODO: merge in the actual assignees here
    staff?.forEach((staffInfo) => {
      if (assignees.has(staffInfo.user.uuid) || !staffInfo.isActive) return;
      assignees.set(staffInfo.user.uuid, staffInfo.user);
    });
    return assignees;
  }, [staff, meProfile]);

  return (
    <Stack direction="row" alignItems="center" gap={0.75}>
      <Typography level="body-sm">Assignee: </Typography>
      <Select
        size="sm"
        onChange={(_, value) => {
          if (value === "") {
            navigate({
              search: (prev) => ({ ...prev, assignee: undefined }),
              replace: true,
            });
          } else {
            navigate({
              search: (prev) => ({ ...prev, assignee: value }),
              replace: true,
            });
          }
        }}
        value={selectedAssignee ?? ""}
        renderValue={(value) =>
          value?.value ? (
            <StaffItem
              nameOverride={meProfile?.uuid === value.value ? "Me" : undefined}
              staffInfo={{ user: assigneeChoices.get(value.value) }}
            />
          ) : (
            "Anyone"
          )
        }
      >
        <Option value="">Anyone</Option>
        {Array.from(assigneeChoices.entries(), ([_, assignee]) => (
          <Option key={assignee.uuid} value={assignee.uuid}>
            <StaffItem
              staffInfo={{ user: assignee }}
              nameOverride={meProfile?.uuid === assignee.uuid ? "Me" : undefined}
            />
          </Option>
        ))}
      </Select>
    </Stack>
  );
};

const CalendarToolbar = () => {
  return (
    <Stack
      direction="row"
      sx={{
        padding: 1,
        borderBottom: "1px solid",
        borderBottomColor: "neutral.200",
      }}
    >
      <MonthSwitcher />
      <Box sx={{ flex: 1 }}></Box>
      <Stack direction="row">
        <AssigneeSelector />
        {/* <TypeSelector />
        <GymSelector /> */}
      </Stack>
    </Stack>
  );
};

const useCalendarTaskSets = () => {
  const currentVenueUuid = useCurrentVenueUuid();
  const { month, assignee } = useCalendarSearchParams();
  const select = useCallback(
    (data: ApiTaskSet[]) => {
      if (assignee) {
        return data.filter((set) => set.assignees.some((a) => a.uuid === assignee));
      }
      return data;
    },
    [assignee],
  );
  return useVenueTaskSets(currentVenueUuid, month, select);
};

const SetCalendar = () => {
  const { setContextMenu, setNewSetValues } = useCalendar();
  const { month, createSet, competition } = useCalendarSearchParams();
  const { data: sets } = useCalendarTaskSets();
  const navigate = useNavigate({ from: "/gym/$venueUuid/schedule/calendar" });

  useEffect(() => {
    if (createSet) {
      setNewSetValues({
        date: format(new Date(), "yyyy-MM-dd"),
        competition,
        type: competition ? "competition" : undefined,
      });
      navigate({
        search: (prev) => ({
          ...prev,
          createSet: undefined,
          competition: undefined,
        }),
      });
    }
  }, [createSet, navigate, setNewSetValues, competition]);

  const [sidebarState, setSidebarState] = useState<
    | {
        events: ApiTaskSet[];
        date: string;
      }
    | undefined
  >(undefined);

  return (
    <Stack direction="row" sx={{ height: "100dvh", flex: 1 }}>
      <Stack sx={{ flex: 1 }}>
        <CalendarToolbar />
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
          }}
        >
          <CalendarContextMenu />
          <Calendar
            onDayContextMenu={(e, date) => {
              if (date >= format(new Date(), "yyyy-MM-dd")) {
                setContextMenu({ date, x: e.clientX, y: e.clientY });
              }
            }}
            events={sets || []}
            month={month}
            renderEvent={({ event }) => {
              const now = new Date();
              const isInPast = event.endDate < format(now, "yyyy-MM-dd");
              const isInOtherMonth =
                event.endDate.slice(0, 7) !== month || event.startDate.slice(0, 7) !== month;
              return (
                <Box sx={{ opacity: isInPast || isInOtherMonth ? 0.5 : 1 }}>
                  <TasksetEventCard
                    taskSet={event}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setContextMenu({
                        taskSet: event,
                        x: e.clientX,
                        y: e.clientY,
                      });
                    }}
                  />
                </Box>
              );
            }}
            renderEmptyDay={({ date }) => (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  ".MuiIconButton-root": {
                    display: "none",
                  },
                  "&:hover .MuiIconButton-root": {
                    display: "flex",
                  },
                  width: "100%",
                }}
                onClick={() =>
                  date >= format(new Date(), "yyyy-MM-dd") ? setNewSetValues({ date }) : null
                }
              >
                {date >= format(new Date(), "yyyy-MM-dd") ? (
                  <IconButton color="primary" variant="solid" sx={{ borderRadius: "100%" }}>
                    <Add />
                  </IconButton>
                ) : null}
              </Box>
            )}
            collapseHeightThreshold={120}
            renderExtraEvents={({ events, date }) => {
              const isInPast = date < format(new Date(), "yyyy-MM-dd");
              const isInOtherMonth = date.slice(0, 7) !== month;
              const color = events.every((event) => event.climbType === events[0].climbType)
                ? taskSetColorMap[events[0].climbType || ""]
                : "#8B93A7";
              return (
                <Box
                  sx={{
                    backgroundColor: Color(color).alpha(0.1).string(),
                    border: "1px solid",
                    borderColor: color,
                    fontSize: "xs",
                    fontWeight: "bold",
                    borderRadius: 8,
                    paddingX: 0.5,
                    width: "100%",
                    marginX: 0.5,
                    marginBottom: 0.5,
                    opacity: isInPast || isInOtherMonth ? 0.5 : 1,
                    cursor: "pointer",
                  }}
                  onClick={() => setSidebarState({ events, date })}
                >
                  +{events.length} more
                </Box>
              );
            }}
          />
        </Box>
      </Stack>
      <Sidebar
        events={sidebarState?.events}
        date={sidebarState?.date}
        onCloseEvents={() => setSidebarState(undefined)}
      />
    </Stack>
  );
};

const SetCalendarWrapper = () => {
  return (
    <CalendarContextProvider>
      <SetCalendar />
    </CalendarContextProvider>
  );
};

export default SetCalendarWrapper;
